import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import { hcApi, hcError } from './api';
import { useHelpConnect } from './HelpConnectAuth';
import { HcAuthShell } from './HcAuthShell';

export const HcInput = ({ icon: Icon, type = 'text', reveal, testId, ...props }) => {
  const [show, setShow] = useState(false);
  const isPw = type === 'password';
  return (
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
      <input
        {...props}
        type={isPw && show ? 'text' : type}
        data-testid={testId}
        className="w-full h-14 pl-12 pr-12 rounded-xl border border-gray-300 bg-white text-[#363636] placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#922A8C]/40 focus:border-[#922A8C] transition-shadow"
      />
      {isPw && reveal && (
        <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#922A8C]"
          aria-label={show ? 'Hide password' : 'Show password'} data-testid={`${testId}-toggle`}>
          {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      )}
    </div>
  );
};

export const HcPrimaryButton = ({ loading, children, ...props }) => (
  <button {...props} disabled={loading || props.disabled}
    className="w-full h-14 rounded-xl bg-[#922A8C] hover:bg-[#7a2275] text-white text-lg font-semibold flex items-center justify-center gap-3 transition-colors disabled:opacity-60">
    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{children}<ArrowRight className="w-5 h-5" /></>}
  </button>
);

export default function HcLoginPage() {
  const { signIn } = useHelpConnect();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await hcApi.post('/auth/login', { email, password });
      await signIn(data.access_token);
      navigate(loc.state?.from?.startsWith('/helpconnect') ? loc.state.from : '/helpconnect', { replace: true });
    } catch (err) {
      setError(hcError(err, 'Sign in failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <HcAuthShell title="Welcome to HELPConnect" subtitle="Your pathway to learning, growth, and opportunity."
      intro="Access your courses, track your progress, and develop the skills needed to achieve your career goals.">
      <form onSubmit={submit} className="space-y-4" data-testid="hc-login-form">
        <HcInput icon={Mail} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" testId="hc-login-email" />
        <HcInput icon={Lock} type="password" reveal placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" testId="hc-login-password" />
        {error && <p className="text-sm text-red-600" role="alert" data-testid="hc-login-error">{error}</p>}
        <HcPrimaryButton type="submit" loading={loading} data-testid="hc-login-submit">Sign In</HcPrimaryButton>
      </form>
      <div className="flex items-center justify-between mt-5 text-[#922A8C] font-medium">
        <Link to="/helpconnect/forgot-password" className="hover:underline" data-testid="hc-forgot-link">Forgot your password?</Link>
        <span className="text-gray-300">|</span>
        <span className="text-sm text-gray-500" data-testid="hc-help-text">Need help? Contact your case manager</span>
      </div>
    </HcAuthShell>
  );
}
